import { PageableResult } from './common';
import {
  HealthSystemDomainFarmsResultFarmViewModel,
  HealthSystemDomainHealthInjectorsDtosHealthInjectorManufacturerDto
} from '../api/HealthSystemApi';

export interface DeviceFirmwareViewModel {
  id: string;
  version: string;
  info: string;
  fileName: string;
  dateTime: Date;
  deviceFirmwareType: DeviceFirmwareType;
}

export interface DeviceFirmwareQueryResult extends PageableResult<DeviceFirmwareViewModel> {}

export enum DeviceFirmwareType {
  Application = 100,
  Bootloader = 200,
  Bluetooth = 300,
  RFID = 400
}

export enum DeviceFirmwareReleaseType {
  Release = 'R',
  Debug = 'D',
  Testing = 'T'
}

export enum DeviceFirmwareRfidType {
  LF,
  UHF
}

/**
 * The RFID type is not a field of the firmware: the backend's RfidType filter matches the version suffix, so
 * "T3.3.2UHF" is UHF and a version with neither suffix has no RFID type.
 */
export const getFirmwareRfidType = (version?: string | null): string | null =>
  Object.keys(DeviceFirmwareRfidType)
    .filter((key) => Number.isNaN(Number(key)))
    .find((key) => version?.endsWith(key)) ?? null;

/** The farm an injector is assigned to, as the injector details response carries it. */
export type HealthInjectorFarm = Pick<HealthSystemDomainFarmsResultFarmViewModel, 'id' | 'number' | 'name'>;

export interface HealthInjectorViewModel {
  id: string;
  macAddress: string;
  comment: string;
  created: Date;
  updated: Date;
  hasErrors: boolean;
  disabled: boolean;
  /** Null when the injector is not assigned to a farm. */
  farm: HealthInjectorFarm | null;
  version: HealthInjectorVersion;
  manufacturer: HealthSystemDomainHealthInjectorsDtosHealthInjectorManufacturerDto | null;
}

export interface ReaderActionViewModel {
  number: string;
  hookId: string;
  dateTime: Date;
  details: ReaderActionDetails;
}

export interface ReaderActionDetails {
  id: string;
  farm: string;
  message: string;
  weight: string;
}

//#region HI version
export interface HealthInjectorVersion {
  main: MainVersion;
  rfid: RfidVersion;
  hardware: HardwareVersion;
  bluetooth: BluetoothVersion;
}

export interface MainVersion {
  mbr: string;
  bootloader: string;
  application: string;
  releaseType: string;
  suffix: string;
}

export interface RfidVersion {
  softDevice: string;
  bootloader: string;
  application: string;
}

export interface BluetoothVersion {
  application: string;
}

export interface HardwareVersion {
  hardware: string;
}
//#endregion HI version

export interface HealthInjectorsQueryResult extends PageableResult<HealthInjectorViewModel> {}

export interface ReaderActionsQueryResult extends PageableResult<ReaderActionViewModel> {
  numberReadRate: number;
  hookReadRate: number;
}

export interface CreateUpdateHealthInjectorCommand {
  id: string;
  macAddress: string;
  comment: string;
  hasErrors: boolean;
  disabled: boolean;
  farmId: string;
  manufacturer: HealthSystemDomainHealthInjectorsDtosHealthInjectorManufacturerDto | null;
}
